import React, { useState, useMemo } from 'react';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';
import TransactionRow from './TransactionRow';
import { TABLE_STYLES } from '../styles/dataTableStyles';
import { AVAILABLE_COLUMNS as TRANSACTION_COLUMNS } from '../config/transactionColumns';

/**
 * Transactions table - same design, layout, colors, and pagination (Load More) as EventsTable.
 * Shared TABLE_STYLES used so Sessions/Users tables can match later.
 */
const OnchainTransactionsTable = ({
  transactions,
  loading,
  loadingMore,
  error,
  totalTransactions,
  hasMore,
  onLoadMore,
  columnConfig = null
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const styles = TABLE_STYLES;

  const visibleColumnKeys = useMemo(() => {
    if (columnConfig && columnConfig.enabled && columnConfig.order) {
      return columnConfig.order.filter(key => columnConfig.enabled.includes(key));
    }
    return ['contract_name_chain', 'function_name', 'transaction_time'];
  }, [columnConfig]);

  const toggleRow = (txHash) => {
    const next = new Set(expandedRows);
    if (next.has(txHash)) next.delete(txHash);
    else next.add(txHash);
    setExpandedRows(next);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  const getLastTransactionTimestamp = () => {
    if (!transactions || transactions.length === 0) return '';
    const last = transactions[transactions.length - 1];
    const t = last.transaction_time || last.block_time || last.timestamp;
    return formatDateTime(t);
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <LiquidFillLoader size={100} />
          <span className="mt-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading transactions...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: '#ef4444', fontFamily: 'Raleway, sans-serif' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          No transactions found for the selected time period. Try changing the date range or filters.
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Results count - same as Events */}
      <div className="mb-2 px-1">
        <div className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          Showing <span className="font-semibold">{transactions.length}</span> most recent results of{' '}
          {Number(totalTransactions || 0).toLocaleString()} matches
        </div>
      </div>

      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
                {visibleColumnKeys.map((columnKey) => {
                  const def = TRANSACTION_COLUMNS[columnKey];
                  if (!def) return null;
                  const isFirst = columnKey === visibleColumnKeys[0];
                  const isLast = columnKey === visibleColumnKeys[visibleColumnKeys.length - 1];
                  return (
                    <th
                      key={columnKey}
                      className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-gray-100 ${
                        isFirst ? 'text-left' : isLast ? 'text-right' : 'text-center'
                      }`}
                      style={{ color: styles.headerText }}
                    >
                      {def.label}
                    </th>
                  );
                })}
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider bg-gray-100" style={{ color: styles.headerText }} />
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => {
                const txHash = transaction.tx_hash || transaction.txHash || `row-${index}`;
                return (
                  <TransactionRow
                    key={txHash}
                    transaction={transaction}
                    isExpanded={expandedRows.has(txHash)}
                    onToggle={() => toggleRow(txHash)}
                    columns={visibleColumnKeys}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More - same as Events */}
      {hasMore && !loading && !error && transactions.length > 0 && (
        <div className="mt-3">
          <div className="mb-2 flex items-center justify-center">
            <div className="text-xs text-[#475569] text-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
              Showing <span className="font-semibold">{transactions.length}</span> results through {getLastTransactionTimestamp()}
            </div>
          </div>
          <div className="flex items-center justify-center">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontFamily: 'Raleway, sans-serif',
                backgroundColor: loadingMore ? styles.secondaryBg : styles.primaryBg,
                color: loadingMore ? styles.textSecondary : styles.textPrimary,
                border: `1px solid ${styles.borderColor}`
              }}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default OnchainTransactionsTable;
